import { useState, useEffect } from "react";
import { RouterProvider } from "react-router-dom";
import { QueryProvider } from "@/app/providers/QueryProvider";
import { ThemeProvider } from "@/app/providers/ThemeProvider";
import { LanguageProvider } from "@/i18n/LanguageProvider";
import { router } from "@/app/routes";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useAuthStore } from "@/lib/store/authStore";
import { sanitizeCompanyDocname } from "@/lib/store/companyDocname";
import { queryClient } from "@/app/providers/QueryProvider";
import { DetailPageTabsProvider } from "@/contexts/DetailPageTabsContext";
import { Toaster } from "sonner";
import { ActiveDetailPageProvider } from "@/contexts/ActiveDetailPageContext";
import { WarningProvider, WarningModal } from "@/lib/warnings";
import { useAuthSync } from "@/hooks/useAuthSync";
import { SessionInvalidatedOverlay } from "@/components/SessionInvalidatedOverlay";
import { getCPToken, getCPTokenExpiry, decodeJwtExpiry, decodeJwtPayload } from "@/lib/api/controlPlaneClient";
import { clearChatwootSession } from "@/components/ChatwootEmbed/ChatwootEmbed";
import { clearGhostSession } from "@/components/GhostEmbed";
import { clearTwentySession } from "@/components/TwentyCrmEmbed";
import posthog from 'posthog-js';

function App() {
  // CRITICAL: Wait for Zustand persist to hydrate before rendering
  const hasHydrated = useAuthStore((state) => state.hasHydrated);

  // Cross-tab auth sync: detects logout/account-switch/token-refresh in other tabs
  // The hook guards internally against pre-hydration state
  const { authSyncEvent } = useAuthSync();

  // Session invalidation overlay state (local React state, not Zustand)
  const [invalidationType, setInvalidationType] = useState<'logout' | 'account-switch' | null>(null);

  // When a logout or account-switch event fires, immediately clear auth state
  // and show the blocking overlay. Token-refresh silently updates Zustand.
  useEffect(() => {
    if (!authSyncEvent) return;
    if (authSyncEvent.type === 'logout' || authSyncEvent.type === 'account-switch') {
      setInvalidationType(authSyncEvent.type);
      // Clear React Query cache to prevent cross-tenant data leakage (OPS-1534)
      queryClient.clear();
      // Clear Chatwoot session flags so iframe re-authenticates via SSO (OPS-1489)
      clearChatwootSession();
      clearGhostSession();
      clearTwentySession();
      // Clear all auth state to prevent stale API calls.
      // Use setState() directly — NOT logoutControlPlane() which makes a network call.
      useAuthStore.setState({
        isAuthenticated: false,
        username: null,
        cpToken: null,
        cpTokenExpiresAt: null,
        currentTenant: null,
        tenantSlug: null,
        tenantServerUrl: null,
        tenantCompany: null,
        isProvisioned: false,
        storefrontUrl: null,
        frappeUrl: null,
        frappeClient: null,
      });
    }
    // token-refresh: silently update Zustand with new token (Epic 03)
    if (authSyncEvent.type === 'token-refresh') {
      // Skip if overlay is already showing (session invalidated takes priority)
      if (invalidationType) return;
      const token = getCPToken();
      if (!token) return;
      const expiry = getCPTokenExpiry()
        || decodeJwtExpiry(token)
        || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      const payload = decodeJwtPayload(token);
      const identityUpdate: Record<string, string | null> = {};
      if (payload?.user) identityUpdate.username = payload.user as string;
      if (payload?.tenant) identityUpdate.currentTenant = payload.tenant as string;
      // Always assign (nullable): a stale/bad `company` claim (bare tenant_id) must
      // clear a previously-stored wrong value, not leave it in place.
      if (payload?.company) {
        identityUpdate.tenantCompany = sanitizeCompanyDocname(
          payload.company as string,
          payload?.tenant as string | undefined,
        );
      }
      if (payload?.slug) identityUpdate.tenantSlug = payload.slug as string;
      useAuthStore.setState({ cpToken: token, cpTokenExpiresAt: expiry, ...identityUpdate });
      if (payload?.user && import.meta.env.VITE_POSTHOG_KEY) {
        posthog.identify(payload.user as string, {
          email: payload.user as string,
          tenant_id: payload.tenant as string,
          company: payload.company as string,
          slug: payload.slug as string,
        })
        posthog.register({ tenant: payload.tenant as string, source: 'dashboard' })
      }
    }
  }, [authSyncEvent, invalidationType]);

  // PostHog: identify user on initial load (defaultIdentifiedOnly=true on server
  // means events are silently dropped until posthog.identify() is called)
  useEffect(() => {
    if (!hasHydrated || !import.meta.env.VITE_POSTHOG_KEY) return;
    const { username, currentTenant, tenantCompany, tenantSlug } = useAuthStore.getState();
    if (username) {
      posthog.identify(username, {
        email: username,
        tenant_id: currentTenant ?? undefined,
        company: tenantCompany ?? undefined,
        slug: tenantSlug ?? undefined,
      });
      posthog.register({ tenant: currentTenant ?? undefined, source: 'dashboard' });
    }
  }, [hasHydrated]);

  // CRITICAL: Manually trigger hydration on mount
  // This ensures hydration completes BEFORE React Query queries run
  // Required for Cypress compatibility (skipHydration: true)
  useEffect(() => {
    (useAuthStore.persist as any).rehydrate()
      .then(() => {
        // Ensure hasHydrated is set even if onRehydrateStorage doesn't run
        const currentHasHydrated = useAuthStore.getState().hasHydrated;
        if (!currentHasHydrated) {
          useAuthStore.setState({ hasHydrated: true });
        }
      })
      .catch((err: any) => {
        console.error("Failed to rehydrate Zustand store:", err);
        // Always set hasHydrated to true to prevent blank screen
        useAuthStore.setState({ hasHydrated: true });
      });

    // Fallback timeout: ensure app renders even if rehydrate hangs
    const timeoutId = setTimeout(() => {
      const currentHasHydrated = useAuthStore.getState().hasHydrated;
      if (!currentHasHydrated) {
        useAuthStore.setState({ hasHydrated: true });
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, []);


  // Block rendering until hydration completes
  if (!hasHydrated) {
    return null; // or loading screen
  }

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <ThemeProvider>
          <QueryProvider>
            <WarningProvider>
              <DetailPageTabsProvider>
                <ActiveDetailPageProvider>
                  <RouterProvider router={router} />
                  <WarningModal />
                  <Toaster position="bottom-right" richColors />
                  {invalidationType && <SessionInvalidatedOverlay type={invalidationType} />}
                </ActiveDetailPageProvider>
              </DetailPageTabsProvider>
            </WarningProvider>
          </QueryProvider>
        </ThemeProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
